import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PeriodFilter } from "@/components/tokens/PeriodFilter";

const EVENT_KINDS = [
  "gate_decision",
  "hitl_decision",
  "phase_transition",
  "artifact_created",
] as const;

const ALL_VALUE = "__all__";

export interface AuditProjectOption {
  id: string;
  name: string;
  slug: string;
}

export interface AuditFilterBarProps {
  eventKind: string;
  onEventKindChange: (kind: string) => void;
  workItemRef: string;
  onWorkItemRefChange: (ref: string) => void;
  periodDays: number;
  onPeriodDaysChange: (days: number) => void;
  /** Present only on the org page (SD4) — the project tab pins its own (SD3). */
  projectOptions?: AuditProjectOption[];
  projectId?: string;
  onProjectIdChange?: (slug: string) => void;
}

export function AuditFilterBar({
  eventKind,
  onEventKindChange,
  workItemRef,
  onWorkItemRefChange,
  periodDays,
  onPeriodDaysChange,
  projectOptions,
  projectId,
  onProjectIdChange,
}: AuditFilterBarProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Select
        value={eventKind || ALL_VALUE}
        onValueChange={(v) => onEventKindChange(v === ALL_VALUE ? "" : v)}
      >
        <SelectTrigger
          className="h-9 w-[180px]"
          aria-label={t("audit.filters.eventKind")}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>{t("audit.filters.allKinds")}</SelectItem>
          {EVENT_KINDS.map((kind) => (
            <SelectItem key={kind} value={kind}>
              {t(`audit.kinds.${kind}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        value={workItemRef}
        onChange={(e) => onWorkItemRefChange(e.target.value)}
        placeholder={t("audit.filters.workItemRefPlaceholder")}
        className="h-9 w-[160px]"
        aria-label={t("audit.filters.workItemRef")}
      />

      <PeriodFilter value={periodDays} onChange={onPeriodDaysChange} />

      {projectOptions && onProjectIdChange && (
        <Select
          value={projectId || ALL_VALUE}
          onValueChange={(v) =>
            onProjectIdChange(v === ALL_VALUE ? "" : v)
          }
        >
          <SelectTrigger
            className="h-9 w-[180px]"
            aria-label={t("audit.filters.project")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>
              {t("audit.filters.allProjects")}
            </SelectItem>
            {projectOptions.map((p) => (
              <SelectItem key={p.id} value={p.slug}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
